import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import VirtualTryOnSection from './components/VirtualTryOnSection';
// import ComparisonSection from './components/ComparisonSection';
import SharedWishlistSection from './components/SharedWishlistSection';
import HowItWorksSection from './components/HowItWorksSection';
import UploadSection from './components/UploadSection';
import CrossPlatformSection from './components/CrossPlatformSection';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';
import CookieConsent from 'shared/components/layout/CookieConsent';

const Home = () => {
  const { t } = useTranslation();

  useEffect(() => {
    // SEO: Set canonical URL
    const link = document.querySelector("link[rel='canonical']");
    if (link) {
      link.setAttribute('href', window.location.origin);
    } else {
      const newLink = document.createElement('link');
      newLink.setAttribute('rel', 'canonical');
      newLink.setAttribute('href', window.location.origin);
      document.head.appendChild(newLink);
    }

    // Add JSON-LD structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'Likiee',
      description:
        'AI-powered visual search engine for fashion with virtual try-on, product comparison, and smart wishlist features.',
      url: window.location.origin,
      applicationCategory: 'Shopping',
      operatingSystem: 'Web, iOS, Android',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        ratingCount: '10000',
      },
      featureList: [
        'Virtual Try-On',
        'AI Product Comparison',
        'Visual Search',
        'Price Tracking',
        'Shared Wishlists',
        'Browser Extension',
      ],
    });
    document.head.appendChild(script);

    return () => {
      // Cleanup structured data on unmount
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>
          {t('home_meta_title', 'Likiee - AI Visual Shopping Assistant | Try On, Find, Compare Fashion with AI')}
        </title>
        <meta
          name='description'
          content={t(
            'home_meta_description',
            'Upload any fashion photo and let AI work its magic. Likiee finds complete looks, lets you virtually try on clothes, compares products intelligently, and tracks prices across thousands of stores. Shop smarter with AI technology.',
          )}
        />
        <meta
          name='keywords'
          content='AI shopping assistant, virtual try-on, visual fashion search, AI look finder, product comparison AI, price tracking, fashion AI, image recognition shopping, smart wishlist, AI fashion recommendations, compare products with AI, complete outfit finder'
        />

        {/* Open Graph tags for social sharing */}
        <meta property='og:title' content='Likiee - AI Visual Shopping Assistant | Try On, Find, Compare Fashion' />
        <meta
          property='og:description'
          content='Upload fashion photos and let AI find complete looks, try clothes on virtually, and compare products intelligently. Smart shopping powered by AI.'
        />
        <meta property='og:type' content='website' />
        <meta property='og:url' content={window.location.href} />
        <meta property='og:image' content={`${window.location.origin}/images/hero-image.png`} />

        {/* Twitter Card tags */}
        <meta name='twitter:card' content='summary_large_image' />
        <meta name='twitter:title' content='Likiee - AI Visual Shopping Assistant' />
        <meta
          name='twitter:description'
          content='Upload fashion photos and let AI find complete looks, virtual try-on, and smart comparisons.'
        />

        {/* Additional SEO tags */}
        <meta name='robots' content='index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' />
        <meta name='author' content='Likiee GmbH' />
        <link rel='canonical' href={window.location.origin} />
      </Helmet>

      <main className='home-page' role='main'>
        <HeroSection />
        <FeaturesSection />
        <VirtualTryOnSection />
        {/* <ComparisonSection /> */}
        <SharedWishlistSection />
        <HowItWorksSection />
        <UploadSection />
        <CrossPlatformSection />
        <FAQSection />
        <CTASection />
        <CookieConsent />
      </main>
    </>
  );
};

export default Home;
